import { Duration, LocalDateTime } from "@js-joda/core";
import React from "react";
import { VisitInstanceId } from "../../messages";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { AppButton } from "../Generic/AppButton";
import { AppCard, AppCardContent, AppCardHeader, AppCardTitle } from "../Generic/AppCard";
import AppModal from "../Generic/AppModal/AppModal";
import Flex from "../Generic/Flex";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import Scaffold from "../Scaffold/Scaffold";
import SignatureBox from "../SignatureBox/SignatureBox";
import ChooseTimesheetClockTimes from "./components/ChooseTimesheetClockTimes";
import DutyTasksList from "./components/DutyTasksList";
import TimesheetVisitCard from "./components/TimesheetVisitCard";
import { Timesheet } from "./TimesheetActivity";
import useTimesheetForm, { ValidTimesheetForm } from "./useTimesheetForm";
import Checkbox from "../Generic/Checkbox";
import styled from "@emotion/styled";
import RadioBoxGroup from "../Auth/components/RadioBoxGroup";

const MIN_DURATIONS_DIFF_MINUTES = 15;

interface Props {
  visitInstanceId: VisitInstanceId;
  patientName: string;
  visitStartTime: LocalDateTime;
  visitEndTime: LocalDateTime;
  timesheet: Timesheet;
  isMedflytCaregiver: boolean;
  onClickVisit: (visitInstanceId: VisitInstanceId) => void;
  onSubmit: (form: ValidTimesheetForm) => void;
  onClickGoBack: () => void;
}

const TimesheetScreen = (props: Props) => {
  const form = useTimesheetForm({ initialState: props.timesheet, onSubmit: props.onSubmit });
  const [patientCanSign, setPatientCanSign] = React.useState<boolean>(
    form.state.patientNotSignReason === null
  );

  const isManualPatientReason =
    form.state.patientNotSignReason !== null &&
    ![
      translate(STRINGS.PATIENT_CANT_PHISICALLY_SIGN),
      translate(STRINGS.PATIENT_REFUSES_TO_SIGN),
    ].includes(form.state.patientNotSignReason);

  const handleClickVisitCard = () => {
    return props.onClickVisit(props.visitInstanceId);
  };

  const handlePatientSign = (value: string | null) => {
    if (patientCanSign) {
      form.actions.setPatientSignature(value);
      form.actions.setPatientNotSignReason(null);
    } else {
      form.actions.setPatientSignature(null);
      form.actions.setPatientNotSignReason(value);
    }
  };

  const isDurationDanger = (() => {
    if (form.state.clockInTime === null || form.state.clockOutTime === null) {
      return false;
    }
    const scheduleDuration = Duration.between(props.visitStartTime, props.visitEndTime);
    const clockDuration = Duration.between(form.state.clockInTime, form.state.clockOutTime);
    const durationsDiffMinutes = scheduleDuration.minus(clockDuration).abs().toMinutes();
    return durationsDiffMinutes > MIN_DURATIONS_DIFF_MINUTES;
  })();

  return (
    <Scaffold
      title={<ScaffoldTitle label={translate(STRINGS.SUBMIT_DUTIES)} />}
      footer={
        <AppButton onClick={form.actions.submit}>
          {translate(STRINGS.SUBMIT_MANUAL_TIMESHEET)}
        </AppButton>
      }
      className="TimesheetScreen"
      onClickGoBack={props.onClickGoBack}
    >
      <AppCard className="TimesheetScreen-section" onClick={handleClickVisitCard}>
        <AppCardHeader>
          <AppCardTitle>{translate(STRINGS.SELECTED_VISIT)}</AppCardTitle>
        </AppCardHeader>
        <TimesheetVisitCard
          patientName={props.patientName}
          visitStartTime={props.visitStartTime}
          visitEndTime={props.visitEndTime}
        />
      </AppCard>

      <AppCard className="TimesheetScreen-section">
        <AppCardHeader>
          <AppCardTitle>{translate(STRINGS.CHOOSE_CLOCK_TIMES)}</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <ChooseTimesheetClockTimes
            clockedInAt={form.state.clockInTime}
            clockedOutAt={form.state.clockOutTime}
            isDurationDanger={isDurationDanger}
            onChooseClockInTime={form.actions.setClockInTime}
            onChooseClockOutTime={form.actions.setClockOutTime}
          />
        </AppCardContent>
      </AppCard>

      <AppCard className="TimesheetScreen-section">
        <AppCardHeader>
          <AppCardTitle>{translate(STRINGS.TASKS)}</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <DutyTasksList
            duties={props.timesheet.requiredDutyTasks}
            selectedDutyTasks={form.state.selectedDuties}
            onChangeNotes={form.actions.setNotes}
            onCheck={form.actions.checkTask}
          />
        </AppCardContent>
      </AppCard>

      <AppCard className="TimesheetScreen-section">
        <AppCardHeader>
          <AppCardTitle>{translate(STRINGS.PATIENT_SIGNATURE)}</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <Flex direction="column" yGap={8}>
            <Flex direction="row" alignItems="center" xGap={8}>
              <Checkbox
                onClick={() => setPatientCanSign(!patientCanSign)}
                checked={!patientCanSign}
              ></Checkbox>
              <div>{translate(STRINGS.PATIENT_CANT_SIGN)}</div>
            </Flex>
            {!patientCanSign && props.isMedflytCaregiver ? (
              <RadioBoxGroup direction="column" onValueChange={handlePatientSign}>
                <RadioBoxGroup.Item
                  value={translate(STRINGS.PATIENT_CANT_PHISICALLY_SIGN)}
                  checked={
                    form.state.patientNotSignReason ===
                    translate(STRINGS.PATIENT_CANT_PHISICALLY_SIGN)
                  }
                >
                  {translate(STRINGS.PATIENT_CANT_PHISICALLY_SIGN)}
                </RadioBoxGroup.Item>

                <RadioBoxGroup.Item
                  value={translate(STRINGS.PATIENT_REFUSES_TO_SIGN)}
                  checked={
                    form.state.patientNotSignReason === translate(STRINGS.PATIENT_REFUSES_TO_SIGN)
                  }
                >
                  {translate(STRINGS.PATIENT_REFUSES_TO_SIGN)}
                </RadioBoxGroup.Item>

                <TimesheetScreen.ManualReason
                  checked={isManualPatientReason}
                  value={
                    isManualPatientReason && form.state.patientNotSignReason !== null
                      ? form.state.patientNotSignReason
                      : ""
                  }
                >
                  <TimesheetScreen.InputReason
                    type="text"
                    placeholder={translate(STRINGS.OTHER_REASON)}
                    value={
                      isManualPatientReason && form.state.patientNotSignReason !== null
                        ? form.state.patientNotSignReason
                        : ""
                    }
                    onChange={(e) => {
                      handlePatientSign(e.target.value);
                    }}
                  ></TimesheetScreen.InputReason>
                </TimesheetScreen.ManualReason>
              </RadioBoxGroup>
            ) : (
              <SignatureBox
                placeholder={translate(STRINGS.PATIENT_SIGNATURE)}
                initialData={form.state.patientSignature}
                onSubmit={handlePatientSign}
              />
            )}
          </Flex>
        </AppCardContent>
      </AppCard>

      <AppCard className="TimesheetScreen-section">
        <AppCardHeader>
          <AppCardTitle>{translate(STRINGS.CAREGIVER_SIGNATURE)}</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <Flex direction="column" yGap={8}>
            <SignatureBox
              placeholder={translate(STRINGS.CAREGIVER_SIGNATURE)}
              initialData={form.state.caregiverSignature}
              onSubmit={form.actions.setCaregiverSignature}
            />
          </Flex>
        </AppCardContent>
      </AppCard>

      <AppModal {...form.modalProps} />
    </Scaffold>
  );
};

TimesheetScreen.ManualReason = styled(RadioBoxGroup.Item)`
  padding: 0;
`;
TimesheetScreen.InputReason = styled.input`
  width: 100%;
  height: 40px;
  border-radius: 4px;
  padding: 0 8px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  border: none;
`;
export default TimesheetScreen;
